import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for speech recognition using Deepgram (primary) with Web Speech API fallback
 * Features:
 * - Continuous listening mode
 * - Auto-restart after TTS completes
 * - Hindi + English support
 * - Fallback to Web Speech API if Deepgram fails
 */
export const useSpeechRecognition = (options = {}) => {
  const {
    onTranscript = () => {},
    onInterimTranscript = () => {},
    continuous = true,
    language = 'hi', // Hindi primary, also detects English
    autoRestart = true
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [useDeepgram, setUseDeepgram] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const deepgramSocketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const webSpeechRef = useRef(null);
  const shouldRestartRef = useRef(false);
  const silenceTimeoutRef = useRef(null);

  // Get Deepgram API key from environment
  const DEEPGRAM_API_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;

  // Initialize Web Speech API as fallback
  const initWebSpeech = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Web Speech API not supported');
      return null;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = language === 'hi' ? 'hi-IN' : 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (interim) {
        setInterimTranscript(interim);
        onInterimTranscript(interim);
      }

      if (final) {
        setTranscript(final);
        onTranscript(final);
        setInterimTranscript('');
      }
    };

    recognition.onerror = (event) => {
      console.error('Web Speech error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(event.error);
      }
    };

    recognition.onend = () => {
      if (shouldRestartRef.current && autoRestart) {
        setTimeout(() => {
          try {
            recognition.start();
          } catch (e) {
            console.log('Could not restart Web Speech');
          }
        }, 100);
      } else {
        setIsListening(false);
      }
    };

    return recognition;
  }, [continuous, language, autoRestart, onTranscript, onInterimTranscript]);

  // Initialize Deepgram WebSocket
  const initDeepgram = useCallback(async () => {
    if (!DEEPGRAM_API_KEY) {
      console.warn('Deepgram API key not found, using Web Speech API fallback');
      setUseDeepgram(false);
      return false;
    }

    try {
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        }
      });
      streamRef.current = stream;

      // Create WebSocket connection to Deepgram
      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?` +
        `model=nova-2&` +
        `language=${language}&` +
        `punctuate=true&` +
        `interim_results=true&` +
        `endpointing=300&` +
        `utterance_end_ms=1000&` +
        `vad_events=true`,
        ['token', DEEPGRAM_API_KEY]
      );

      socket.onopen = () => {
        console.log('Deepgram connected');
        setIsInitialized(true);
        
        // Start MediaRecorder
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus'
        });
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(event.data);
          }
        };

        mediaRecorder.start(250); // Send chunks every 250ms
        mediaRecorderRef.current = mediaRecorder;
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'Results') {
          const transcript = data.channel?.alternatives?.[0]?.transcript || '';
          
          if (transcript) {
            if (data.is_final) {
              setTranscript(transcript);
              onTranscript(transcript);
              setInterimTranscript('');
              
              // Reset silence timeout
              if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
              }
            } else {
              setInterimTranscript(transcript);
              onInterimTranscript(transcript);
            }
          }
        }
      };

      socket.onerror = (error) => {
        console.error('Deepgram error:', error);
        setError('Deepgram connection failed');
        // Fallback to Web Speech
        setUseDeepgram(false);
      };

      socket.onclose = (event) => {
        console.log('Deepgram disconnected:', event.code);
        if (shouldRestartRef.current && autoRestart) {
          setTimeout(() => initDeepgram(), 1000);
        } else {
          setIsListening(false);
        }
      };

      deepgramSocketRef.current = socket;
      return true;
    } catch (err) {
      console.error('Deepgram init error:', err);
      setUseDeepgram(false);
      return false;
    }
  }, [DEEPGRAM_API_KEY, language, autoRestart, onTranscript, onInterimTranscript]);

  // Start listening
  const startListening = useCallback(async () => {
    setError(null);
    shouldRestartRef.current = true;

    if (useDeepgram && DEEPGRAM_API_KEY) {
      const success = await initDeepgram();
      if (success) {
        setIsListening(true);
        return;
      }
    }

    // Fallback to Web Speech API
    if (!webSpeechRef.current) {
      webSpeechRef.current = initWebSpeech();
    }

    if (webSpeechRef.current) {
      try {
        webSpeechRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Web Speech start error:', err);
        setError('Could not start speech recognition');
      }
    }
  }, [useDeepgram, DEEPGRAM_API_KEY, initDeepgram, initWebSpeech]);

  // Stop listening
  const stopListening = useCallback(() => {
    shouldRestartRef.current = false;

    // Stop Deepgram
    if (deepgramSocketRef.current) {
      deepgramSocketRef.current.close();
      deepgramSocketRef.current = null;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop Web Speech
    if (webSpeechRef.current) {
      webSpeechRef.current.stop();
    }

    setIsListening(false);
    setInterimTranscript('');
  }, []);

  // Pause listening (for when TTS is speaking)
  const pauseListening = useCallback(() => {
    shouldRestartRef.current = false;
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
    }
    
    if (webSpeechRef.current) {
      try {
        webSpeechRef.current.stop();
      } catch (e) {}
    }
  }, []);

  // Resume listening (after TTS finishes)
  const resumeListening = useCallback(() => {
    shouldRestartRef.current = true;
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
    } else if (webSpeechRef.current && !isListening) {
      try {
        webSpeechRef.current.start();
        setIsListening(true);
      } catch (e) {
        console.log('Could not resume Web Speech');
      }
    }
  }, [isListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldRestartRef.current = false;
      stopListening();
    };
  }, [stopListening]);

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    useDeepgram,
    isInitialized,
    startListening,
    stopListening,
    pauseListening,
    resumeListening
  };
};

export default useSpeechRecognition;
